import { useMemo, type ReactNode } from 'react';
import { Box, ThemeProvider, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { CONTENT_FONT_FAMILY_VAR } from '../constants/fonts';
import { createContentTheme } from './createContentTheme';

interface ContentThemeProviderProps {
  children: ReactNode;
}

/**
 * Applies the ZERO content theme (purple accent, slate / #F1F5F9 canvas,
 * Readex Pro type scale, flat surfaces, operational status tones) to
 * everything rendered inside the shell tile.
 *
 * Mode and direction are read from the shell theme so the dark-mode toggle and
 * RTL switch keep working; the shell itself (sidebar, app bar, account menus)
 * never sees this theme. Portals opened from the page inherit it through React
 * context. Deliberately no `CssBaseline` here: it would restyle `<body>` and
 * leak the content font into the shell.
 */
export function ContentThemeProvider({ children }: ContentThemeProviderProps) {
  const shellTheme = useTheme();
  const { i18n } = useTranslation();
  const mode = shellTheme.palette.mode;
  const direction = shellTheme.direction;
  const latin = (i18n.language || 'en').startsWith('en');

  const theme = useMemo(
    () => createContentTheme(mode, direction, { latin }),
    [mode, direction, latin],
  );

  return (
    <ThemeProvider theme={theme}>
      <ContentSurface>{children}</ContentSurface>
    </ThemeProvider>
  );
}

/**
 * Full-bleed canvas painted over the shell tile (which keeps its own 24px
 * padding and 16px corners). No transform / filter / contain here: the report
 * PDF capture and the map's fixed-position overlays rely on the normal
 * containing block.
 */
function ContentSurface({ children }: { children: ReactNode }) {
  return (
    <Box
      className="zero-content-surface"
      sx={{
        m: -3,
        px: { xs: 2, sm: 3, lg: 4 },
        py: { xs: 2.5, sm: 3, lg: 4 },
        borderRadius: '16px',
        bgcolor: 'background.default',
        color: 'text.primary',
        fontFamily: CONTENT_FONT_FAMILY_VAR,
        minHeight: { xs: 'calc(100vh - 80px)', sm: 'calc(100vh - 32px)' },
        transition: 'background-color 200ms ease',
      }}
    >
      <Box sx={{ maxWidth: 1600, mx: 'auto', minWidth: 0 }}>{children}</Box>
    </Box>
  );
}
